import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FieldPath,
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';

import { Accordion } from '@radix-ui/react-accordion';
import { get, isEqual, set } from 'lodash';
import {
  Building2,
  CircleDollarSignIcon,
  CircleUserRound,
  Info,
  TruckIcon,
  Weight,
} from 'lucide-react';

import { Button } from 'components/Button';
import LoadContextProvider from 'components/LoadContextProvider';
import TMSProvider from 'components/TMSProvider';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useAuth } from 'hooks/useAuth';
import { useGuardedCreateLoad } from 'hooks/useGuardedCreateLoad';
import { useLoadContext } from 'hooks/useLoadContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { useToast } from 'hooks/useToaster';
import { CreateLoadRequest } from 'lib/api/createLoad';
import { getCarriers } from 'lib/api/getCarriers';
import { getCustomers } from 'lib/api/getCustomers';
import { getLocations } from 'lib/api/getLocations';
import { updateTMS } from 'lib/api/updateTMS';
import { FormStorageService } from 'lib/services/FormStorage/service';
import { LoadSectionAccordionItem } from 'pages/LoadView/LoadInformation/Components';
import { CarrierSectionForm } from 'pages/LoadView/LoadInformation/Sections/CarrierSectionForm';
import { CustomerSectionForm } from 'pages/QuoteView/LoadBuilding/McleodSectionForms/Customer';
import { McleodRouteSection } from 'pages/QuoteView/LoadBuilding/McleodSectionForms/McleodRouteSection';
import { OperatorSectionForm } from 'pages/QuoteView/LoadBuilding/McleodSectionForms/Operator';
import { RatesForm } from 'pages/QuoteView/LoadBuilding/McleodSectionForms/Rates';
import { SpecificationsForm } from 'pages/QuoteView/LoadBuilding/McleodSectionForms/Specifications';
import { hasNoErrorsInSubsection } from 'pages/QuoteView/LoadBuilding/McleodSectionForms/Stop';
import { Tenant } from 'pages/QuoteView/LoadBuilding/McleodSectionForms/constants';
import {
  convertModeToSelectValue,
  convertOrderTypeLabelToCode,
  convertOrderTypeToSelectValue,
  convertTransportTypeToSelectValue,
  getMcleodIntegrationFromList,
  getTransportTypeOptions,
  normalizeMcleodTransportType,
} from 'pages/QuoteView/LoadBuilding/McleodSectionForms/helpers';
import {
  buildMcleodStopsFromPickupAndConsignee,
  ensureMcleodRouteStops,
  isMcleodMultiStopRoute,
  mcleodActiveStopTabFromErrors,
  mcleodRouteEndsHaveContent,
  normalizeMcleodRouteStopTypes,
  syncMcleodRouteStopsToPickupConsignee,
  validateMcleodMonotonicRouteStopTimes,
} from 'pages/QuoteView/LoadBuilding/McleodSectionForms/mcleodRouteStops';
import LoadBuildingAccordionControls from 'pages/QuoteView/LoadBuilding/components/LoadBuildingAccordionControls';
import {
  LoadCreatedMessage,
  type SharepointAgileCreatedSummary,
} from 'pages/QuoteView/LoadBuilding/components/LoadCreatedMessage';
import { useLoadBuildingAccordionTabs } from 'pages/QuoteView/LoadBuilding/hooks/useLoadBuildingAccordionTabs';
import { validateDuplicateRouteStopLocations } from 'pages/QuoteView/LoadBuilding/utils/stopValidation';
import { suggestedTransportTypeOrEmpty } from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import {
  CompanyCoreInfo,
  Customer,
  Dropoff,
  Load,
  LoadCarrier,
  LoadStopsItem,
  NormalizedDropoff,
  NormalizedLoad,
  NormalizedPickup,
  Pickup,
  RateData,
  Specifications,
  TMSCarrier,
  TMSCustomer,
  TMSLocation,
  createInitLoad,
  createInitRateData,
  createInitSpecs,
  normalizeLoad,
} from 'types/Load';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import { TMSOperation, UpdateTMSRequest } from 'types/api/UpdateTMS';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { TMS } from 'types/enums/Integrations';
import {
  type GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import {
  type LoadBuildingSuggestions,
  SuggestedLoad,
} from 'types/suggestions/LoadBuildingSuggestions';
import {
  acceptLoadViewSuggestion,
  useApplyLoadViewSuggestion,
  useFocusLoadViewSuggestion,
} from 'utils/applyLoadViewSuggestion';
import { applyCompleteKitSuggestions } from 'utils/completeKitSuggestion';
import {
  DEFAULT_MAX_VALIDATION_TOAST_LABELS,
  type ManualFieldError,
  applyManualValidationErrors,
  formatFormErrors,
  handleFormErrors,
  loadBuildingManualValidationDisplayOptions,
  scheduleLoadBuildingSuggestionValidation,
} from 'utils/formValidation';
import { concatAddress } from 'utils/formatStrings';
import { objectHasTruthyValues } from 'utils/isValidObject';
import {
  injectSelectedObject,
  normalizeToArray,
  normalizeToStringArray,
  withLoadBuildingSuggestionContext,
} from 'utils/loadInfoAndBuilding';
import { denormalizeDatesForTMSForm } from 'utils/parseDatesForTMSForm';

function sharepointAgileCreatedSummaryFromSuggestion(
  suggestion: MaybeUndef<GenericSuggestion>
): Undef<SharepointAgileCreatedSummary> {
  if (!suggestion || suggestion.pipeline !== SuggestionPipelines.LoadBuilding) {
    return undefined;
  }
  const lb = suggestion as LoadBuildingSuggestions;
  if (!lb.sharepointAgilePasteHint) {
    return undefined;
  }
  const booking =
    lb.suggested.additionalReferences
      ?.find((r) => r.qualifier === 'Booking')
      ?.number?.trim() ?? '';
  const po = lb.suggested.poNums?.trim() ?? '';
  return {
    bookingNumber: booking,
    poNumber: po,
  };
}

enum AvailableTabs {
  customer = 'customer',
  specifications = 'specifications',
  carrier = 'carrier',
  route = 'route',
  rates = 'rates',
  operator = 'operator',
}

const initLoadBuildingForm = (
  tmsName: string,
  user?: Maybe<{ revenue_code?: string; external_tms_id?: string }>
): NormalizedLoad => {
  const baseLoad = normalizeLoad(tmsName, {
    ...createInitLoad(),
    mode: 'TL',
    specifications: { ...createInitSpecs(), transportType: 'V' },
    rateData: {
      ...createInitRateData(),
      revenueCode: user?.revenue_code ?? '',
      salesperson1: user?.external_tms_id ?? '',
      salesperson1Percent: user?.external_tms_id ? 100 : 0,
    },
  });

  return {
    ...baseLoad,
    pickup: {
      ...baseLoad.pickup,
      additionalReferences: [],
    },
    consignee: {
      ...baseLoad.consignee,
      additionalReferences: [],
    },
    stops: buildMcleodStopsFromPickupAndConsignee(
      baseLoad.pickup,
      baseLoad.consignee
    ),
  };
};

const denormalizeStopsForTMSForm = (
  tmsName: string,
  stops: LoadStopsItem[]
): LoadStopsItem[] =>
  stops.map(
    (stop) => denormalizeDatesForTMSForm(tmsName, stop) as LoadStopsItem
  );

function flattenLoadFormFieldPaths(
  values: Record<string, unknown>,
  parentKey = '',
  result: string[] = []
): string[] {
  for (const key of Object.keys(values)) {
    const value = values[key];
    const newKey = parentKey ? `${parentKey}.${key}` : key;

    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      !(value instanceof Date)
    ) {
      flattenLoadFormFieldPaths(
        value as Record<string, unknown>,
        newKey,
        result
      );
    } else {
      result.push(newKey);
    }
  }
  return result;
}

interface UnifiedMcLeodLoadFormProps {
  isCreateMode: boolean;
  load?: Load;
  onSubmit?: (data: NormalizedLoad) => void;
  onSuccess?: (result: any) => void;
  onError?: (error: any) => void;
}

export default function UnifiedMcLeodLoadForm({
  isCreateMode,
  load,
  onSubmit,
  onSuccess,
  onError,
}: UnifiedMcLeodLoadFormProps) {
  const tmsName = TMS.McleodEnterprise;
  // LoadContext defined only in read/update mode, not in create mode
  const loadContext = useLoadContext();

  const enableCarrierSection = !isCreateMode;

  const {
    serviceID,
    tmsIntegrations,
    serviceFeaturesEnabled: { isOperatorEnabled },
  } = useServiceFeatures();
  const mcleodIntegration = getMcleodIntegrationFromList(tmsIntegrations);
  const tmsID = isCreateMode ? mcleodIntegration?.id : load?.tmsID;
  const tenant = mcleodIntegration?.tenant ?? '';
  const { user } = useAuth();
  const transportTypeOptions = useMemo(
    () => getTransportTypeOptions(mcleodIntegration),
    [mcleodIntegration]
  );

  const { toast } = useToast();
  const guardedCreateLoad = useGuardedCreateLoad();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadBuildingSuggestionClicked, setIsLoadBuildingSuggestionClicked] =
    useState(false);
  const [showAIHints, setShowAIHints] = useState(false);

  const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [isLoadingCarriers, setIsLoadingCarriers] = useState(true);
  const [customers, setCustomers] = useState<Maybe<TMSCustomer[]>>(null);
  /** Raw list from GET /locations and location search; never bind dropdown to this alone. */
  const [dataLocations, setDataLocations] =
    useState<Maybe<TMSLocation[]>>(null);
  const [carriers, setCarriers] = useState<Maybe<TMSCarrier[]>>(null);
  const [carrierIsQualified, setCarrierIsQualified] = useState(true);
  const [builtLoad, setBuiltLoad] = useState<Maybe<Load>>(null);
  const [createNewLocForPickup, setCreateNewLocForPickup] = useState(false);
  const [activeRouteStopTab, setActiveRouteStopTab] = useState<
    string | undefined
  >();
  const [routeStopFocusKey, setRouteStopFocusKey] = useState(0);
  const [sharepointAgileCreated, setSharepointAgileCreated] =
    useState<Undef<SharepointAgileCreatedSummary>>(undefined);
  const [loadCreatedBannerPoNums, setLoadCreatedBannerPoNums] =
    useState<Undef<string>>(undefined);
  // Bumped only when edit-mode form reset actually runs (after catalogs load).
  const [editHydrationEpoch, setEditHydrationEpoch] = useState(0);

  const handleActiveRouteStopTabHandled = useCallback(() => {
    setActiveRouteStopTab(undefined);
  }, []);

  const focusRouteStopFromValidationErrors = useCallback(
    (errors: ReadonlyArray<{ path: string }>) => {
      const stopTab = mcleodActiveStopTabFromErrors(errors);
      if (stopTab) {
        setActiveRouteStopTab(stopTab);
        setRouteStopFocusKey((key) => key + 1);
      }
    },
    []
  );

  const allTabs = useMemo(() => {
    const tabs = [
      AvailableTabs.customer,
      AvailableTabs.specifications,
      AvailableTabs.rates,
      AvailableTabs.route,
    ];
    if (enableCarrierSection) {
      tabs.push(AvailableTabs.carrier);
    }
    if (!isCreateMode && isOperatorEnabled) {
      tabs.push(AvailableTabs.operator);
    }
    return tabs;
  }, [enableCarrierSection, isCreateMode, isOperatorEnabled]);

  const {
    activeTabs,
    setActiveTabs,
    expandAll,
    collapseAll,
    defaultView,
    setDefaultView,
  } = useLoadBuildingAccordionTabs({ allTabs });

  const handleAccordionValueChange = useCallback(
    (next: string[]) => {
      setActiveTabs(next);
    },
    [setActiveTabs]
  );

  const {
    currentState: {
      clickedSuggestion,
      threadId,
      emailId,
      threadItemId,
      goToSuggestion,
    },
    setCurrentState,
  } = useContext(SidebarStateContext);

  const clickedSuggestionId = clickedSuggestion?.id ?? null;
  const clickedSuggestionRef = useRef(clickedSuggestion);
  clickedSuggestionRef.current = clickedSuggestion;

  const activeLoadBuildingSuggestion =
    useMemo((): Maybe<LoadBuildingSuggestions> => {
      const suggestion =
        clickedSuggestionId == null ? null : clickedSuggestionRef.current;
      if (suggestion?.pipeline === SuggestionPipelines.LoadBuilding) {
        return suggestion as LoadBuildingSuggestions;
      }
      return null;
    }, [clickedSuggestionId]);

  useEffect(() => {
    if (!isCreateMode) {
      setIsLoadBuildingSuggestionClicked(false);
      return;
    }
    setIsLoadBuildingSuggestionClicked(Boolean(activeLoadBuildingSuggestion));
  }, [isCreateMode, activeLoadBuildingSuggestion]);

  // Only clear the "Load Created" banner when switching suggestions;
  // keep it when the same suggestion is marked Accepted after building.
  useEffect(() => {
    if (!isCreateMode) {
      return;
    }
    setBuiltLoad(null);
    setLoadCreatedBannerPoNums(undefined);
  }, [isCreateMode, activeLoadBuildingSuggestion?.id]);

  const fetchCustomers = async () => {
    if (!tmsID) return;

    setIsLoadingCustomers(true);
    const res = await getCustomers(tmsID);
    if (res.isOk()) {
      setCustomers(res.value.customerList);
    } else {
      toast({
        description: res.error.message || 'Error while fetching customer list.',
        variant: 'destructive',
      });
    }
    setIsLoadingCustomers(false);
  };

  const fetchLocations = async (forceRefresh = false) => {
    if (!tmsID) return;

    setIsLoadingLocations(true);
    const res = await getLocations(tmsID, forceRefresh);
    if (res.isOk()) {
      setDataLocations(res.value.locationList);
      if (forceRefresh) {
        toast({
          description: 'Successfully refreshed location list.',
          variant: 'success',
        });
      }
    } else {
      const errorMessage = forceRefresh
        ? 'Error while refreshing location list.'
        : 'Error while fetching location list.';
      toast({
        description: res.error.message || errorMessage,
        variant: 'destructive',
      });
    }
    setIsLoadingLocations(false);
  };

  const fetchCarriers = async () => {
    if (!tmsID) return;

    setIsLoadingCarriers(true);

    const res = await getCarriers(tmsID);
    if (res.isOk()) {
      setCarriers(res.value.carrierList);
    } else {
      toast({
        description: res.error.message || 'Error while fetching carrier list.',
        variant: 'destructive',
      });
    }
    setIsLoadingCarriers(false);
  };

  const handleRefreshLocations = async () => {
    await fetchLocations(true);
  };

  useEffect(() => {
    if (
      isCreateMode &&
      (!clickedSuggestion ||
        clickedSuggestion.pipeline !== SuggestionPipelines.LoadBuilding)
    ) {
      goToSuggestion({
        suggestionPipeline: SuggestionPipelines.LoadBuilding,
      });
    }
    fetchCustomers();
    fetchLocations();
    if (enableCarrierSection) {
      fetchCarriers();
    }
    // Intentionally keyed on tmsID only — refetch catalogs when TMS changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tmsID]);

  useEffect(() => {
    if (isCreateMode) {
      setShowAIHints(Boolean(activeLoadBuildingSuggestion));
      return;
    }
    if (
      clickedSuggestion &&
      clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
    ) {
      setShowAIHints(true);
    } else {
      setShowAIHints(false);
    }
  }, [isCreateMode, activeLoadBuildingSuggestion, clickedSuggestion]);

  const memoizedDefaultValues: NormalizedLoad = useMemo(() => {
    // Edit mode: use existing load data
    if (!isCreateMode && load) {
      const normalizedLoad = normalizeLoad(tmsName, load);

      let stops = normalizedLoad.stops;
      if (!stops || stops.length < 2) {
        stops = buildMcleodStopsFromPickupAndConsignee(
          normalizedLoad.pickup,
          normalizedLoad.consignee
        );
      }

      const mappedMode = convertModeToSelectValue(normalizedLoad.mode);
      const mappedOrderType = convertOrderTypeToSelectValue(
        normalizedLoad.specifications?.orderType,
        mcleodIntegration
      );
      const mappedTransportType = convertTransportTypeToSelectValue(
        normalizedLoad.specifications?.transportType,
        mcleodIntegration
      );

      return {
        ...normalizedLoad,
        mode: mappedMode,
        specifications: {
          ...normalizedLoad.specifications,
          orderType:
            mappedOrderType ?? normalizedLoad.specifications?.orderType ?? '',
          transportType:
            mappedTransportType ??
            normalizedLoad.specifications?.transportType ??
            '',
        },
        stops,
      };
    }

    // Create mode: check for suggestions or use init data
    if (isCreateMode) {
      if (builtLoad) {
        const normalized = normalizeLoad(tmsName, builtLoad);
        const mappedOrderType = convertOrderTypeToSelectValue(
          normalized.specifications?.orderType,
          mcleodIntegration
        );
        const mappedTransportType = convertTransportTypeToSelectValue(
          normalized.specifications?.transportType,
          mcleodIntegration
        );
        return {
          ...normalized,
          mode: convertModeToSelectValue(normalized.mode),
          specifications: {
            ...normalized.specifications,
            orderType:
              mappedOrderType ?? normalized.specifications?.orderType ?? '',
            transportType:
              mappedTransportType ??
              normalized.specifications?.transportType ??
              '',
          },
          stops:
            normalized.stops && normalized.stops.length >= 2
              ? normalized.stops
              : buildMcleodStopsFromPickupAndConsignee(
                  normalized.pickup,
                  normalized.consignee
                ),
        };
      }

      if (activeLoadBuildingSuggestion) {
        const casted = activeLoadBuildingSuggestion.suggested as SuggestedLoad;
        const collectionMethods = ['prepaid', 'collect', 'third-party', 'cod'];
        // Email named no equipment (or it isn't a tenant option): leave it
        // empty so the rep picks one instead of silently prefilling Van.
        const suggestedMcleodTransportType = normalizeMcleodTransportType(
          suggestedTransportTypeOrEmpty(casted.specifications),
          transportTypeOptions
        );

        const suggestedFields: Load = {
          ...createInitLoad(),
          ...casted,
          mode: 'TL',
          commodities: normalizeToArray(casted.commodities) || [],
          additionalReferences:
            normalizeToArray(casted.additionalReferences) || [],
          notes: normalizeToArray(casted.notes) || [],
          customer: casted.customer
            ? {
                ...casted.customer,
                refNumberCandidates: normalizeToStringArray(
                  casted.customer.refNumberCandidates
                ),
              }
            : casted.customer,
          pickup: casted.pickup
            ? ({
                ...casted.pickup,
                refNumberCandidates: normalizeToStringArray(
                  casted.pickup.refNumberCandidates
                ),
                additionalReferences:
                  normalizeToArray(casted.pickup.additionalReferences) || [],
              } as Pickup)
            : casted.pickup,
          consignee: casted.consignee
            ? ({
                ...casted.consignee,
                refNumberCandidates: normalizeToStringArray(
                  casted.consignee.refNumberCandidates
                ),
                additionalReferences:
                  normalizeToArray(casted.consignee.additionalReferences) || [],
              } as unknown as Dropoff)
            : casted.consignee,
          specifications: {
            ...createInitSpecs(),
            ...casted.specifications,
            transportType: suggestedMcleodTransportType,
          } as Specifications,
          rateData: {
            ...createInitRateData(),
            ...casted.rateData,
            customerLineItems:
              normalizeToArray(casted.rateData?.customerLineItems) || [],
            carrierLineItems:
              normalizeToArray(casted.rateData?.carrierLineItems) || [],
            collectionMethod: collectionMethods.includes(
              casted.rateData?.collectionMethod?.toLowerCase()
            )
              ? casted.rateData.collectionMethod
              : null,
            revenueCode: user?.revenue_code?.trim()
              ? user.revenue_code
              : (casted.rateData?.revenueCode ?? ''),
            salesperson1: user?.external_tms_id ?? '',
            salesperson1Percent: user?.external_tms_id ? 100 : 0,
          } as RateData,
        };

        const normalizedSuggestion = normalizeLoad(tmsName, suggestedFields);
        return {
          ...normalizeLoad(tmsName, createInitLoad()),
          ...normalizedSuggestion,
          mode: 'TL',
          specifications: {
            ...normalizedSuggestion.specifications,
            // normalizeLoad fills '' with 'Truckload'; keep the rep's pick open.
            transportType: suggestedMcleodTransportType,
          },
          // AI multi-stop is out of scope (same as Kenco ENG-7694): always seed
          // route from flat pickup/consignee on suggestion click.
          stops: buildMcleodStopsFromPickupAndConsignee(
            suggestedFields.pickup as NormalizedPickup,
            suggestedFields.consignee as NormalizedDropoff
          ),
        } as NormalizedLoad;
      }

      return initLoadBuildingForm(tmsName, user);
    }

    return initLoadBuildingForm(tmsName, user);
    // Intentionally keyed on suggestion id; full suggestion object would churn defaults.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeLoadBuildingSuggestion?.id,
    threadId,
    tmsName,
    builtLoad,
    isCreateMode,
    load,
    user,
    mcleodIntegration,
    tenant,
    transportTypeOptions,
  ]);

  const formMethods = useForm<NormalizedLoad>({
    defaultValues: memoizedDefaultValues,
  });

  const {
    handleSubmit,
    reset,
    trigger,
    formState: { errors },
  } = formMethods;

  const watchedStops = useWatch({
    control: formMethods.control,
    name: 'stops',
  });
  const watchedPickupForMerge = useWatch({
    control: formMethods.control,
    name: 'pickup',
  });
  const watchedConsigneeForMerge = useWatch({
    control: formMethods.control,
    name: 'consignee',
  });
  const watchedPickupMergeID = watchedPickupForMerge?.externalTMSID;
  const watchedConsigneeMergeID = watchedConsigneeForMerge?.externalTMSID;
  const watchedPickup = watchedPickupForMerge;

  const originalSuggestionStops = useMemo(() => {
    if (!activeLoadBuildingSuggestion) {
      return null;
    }
    const s = activeLoadBuildingSuggestion.suggested as SuggestedLoad;
    return { pickup: s.pickup, consignee: s.consignee };
    // Intentionally keyed on suggestion id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLoadBuildingSuggestion?.id]);

  const prevMergedLocationsRef = useRef<TMSLocation[]>([]);

  /**
   * Merge raw API/search results with AI-suggested stops and current form selections so
   * location search (which replaces `dataLocations`) cannot drop suggested rows from the dropdown.
   */
  const mergedLocations = useMemo(() => {
    let base = [...(dataLocations ?? [])];

    if (originalSuggestionStops?.pickup?.externalTMSID?.trim()) {
      const suggestedPickup = originalSuggestionStops.pickup as TMSLocation;
      if (
        suggestedPickup?.addressLine1 ||
        suggestedPickup?.name ||
        suggestedPickup?.city
      ) {
        base = injectSelectedObject(suggestedPickup, base);
      }
    }

    if (originalSuggestionStops?.consignee?.externalTMSID?.trim()) {
      const suggestedConsignee =
        originalSuggestionStops.consignee as TMSLocation;
      if (
        suggestedConsignee?.addressLine1 ||
        suggestedConsignee?.name ||
        suggestedConsignee?.city
      ) {
        base = injectSelectedObject(suggestedConsignee, base);
      }
    }

    if (watchedPickupForMerge?.externalTMSID?.trim()) {
      const formPickup = watchedPickupForMerge as TMSLocation;
      if (formPickup?.addressLine1 || formPickup?.name || formPickup?.city) {
        base = injectSelectedObject(formPickup, base);
      }
    }

    if (watchedConsigneeForMerge?.externalTMSID?.trim()) {
      const formConsignee = watchedConsigneeForMerge as TMSLocation;
      if (
        formConsignee?.addressLine1 ||
        formConsignee?.name ||
        formConsignee?.city
      ) {
        base = injectSelectedObject(formConsignee, base);
      }
    }

    const isContentEqual = isEqual(base, prevMergedLocationsRef.current);
    if (isContentEqual) {
      return prevMergedLocationsRef.current;
    }

    prevMergedLocationsRef.current = base;
    return base;
    // IDs + dataLocations are enough; full watched objects would churn the merge every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataLocations,
    watchedPickupMergeID,
    watchedConsigneeMergeID,
    originalSuggestionStops?.pickup?.externalTMSID,
    originalSuggestionStops?.consignee?.externalTMSID,
  ]);

  const setLocationsAction = useCallback<
    Dispatch<SetStateAction<Maybe<TMSLocation[]>>>
  >((locationsUpdate) => {
    if (typeof locationsUpdate === 'function') {
      setDataLocations((prev) => {
        const next = locationsUpdate(prev);
        if (isEqual(next, prev)) {
          return prev;
        }
        return next ?? [];
      });
    } else {
      const locationsArray = locationsUpdate ?? [];
      setDataLocations((prev) => {
        if (isEqual(locationsArray, prev)) {
          return prev;
        }
        return locationsArray;
      });
    }
  }, []);

  useEffect(() => {
    const pickup = formMethods.getValues('pickup');
    const consignee = formMethods.getValues('consignee');
    const rawStops = formMethods.getValues('stops') ?? [];
    const ensuredStops = ensureMcleodRouteStops({
      stops: rawStops,
      pickup,
      consignee,
    });

    if (ensuredStops.length < 2) {
      return;
    }

    const rawNormalized = normalizeMcleodRouteStopTypes(rawStops);
    const shouldHydrateStops =
      !mcleodRouteEndsHaveContent(rawNormalized) &&
      mcleodRouteEndsHaveContent(ensuredStops);

    if (shouldHydrateStops) {
      formMethods.setValue('stops', ensuredStops, { shouldDirty: false });
      formMethods.setValue('moreThanTwoStops', ensuredStops.length > 2, {
        shouldDirty: false,
      });
      return;
    }

    if (!mcleodRouteEndsHaveContent(ensuredStops)) {
      return;
    }

    const synced = syncMcleodRouteStopsToPickupConsignee(ensuredStops);
    formMethods.setValue('pickup', synced.pickup, { shouldDirty: false });
    formMethods.setValue('consignee', synced.consignee, { shouldDirty: false });
    formMethods.setValue('moreThanTwoStops', ensuredStops.length > 2, {
      shouldDirty: false,
    });
  }, [formMethods, watchedStops]);

  useEffect(() => {
    const firstStop = watchedStops?.[0];
    if (
      !firstStop?.externalTMSID &&
      mcleodIntegration?.featureFlags.isOnlyCityStateRequired === false &&
      hasNoErrorsInSubsection('stops.0', errors) &&
      objectHasTruthyValues(
        (firstStop?.address ?? {}) as Record<string, unknown>,
        [
          'name',
          'addressLine1',
          'addressLine2',
          'city',
          'state',
          'zip',
          'country',
        ]
      )
    ) {
      setCreateNewLocForPickup(true);
    } else {
      setCreateNewLocForPickup(false);
    }
  }, [
    watchedStops,
    errors,
    mcleodIntegration?.featureFlags.isOnlyCityStateRequired,
  ]);

  const editResetKeyRef = useRef('');
  const loadBuildingStorageRestoreFingerprintRef = useRef<string>('');

  useEffect(() => {
    loadBuildingStorageRestoreFingerprintRef.current = '';
  }, [threadId]);
  const customerExternalTMSID =
    memoizedDefaultValues?.customer?.externalTMSID ?? '';
  const pickupExternalTMSID =
    memoizedDefaultValues?.pickup?.externalTMSID ?? '';
  const consigneeExternalTMSID =
    memoizedDefaultValues?.consignee?.externalTMSID ?? '';
  const editResetKey = [
    load?.ID ?? load?.externalTMSID ?? load?.freightTrackingID ?? '',
    load?.UpdatedAt ?? '',
    customerExternalTMSID,
    pickupExternalTMSID,
    consigneeExternalTMSID,
  ].join('|');

  useEffect(() => {
    if (!isLoadingCustomers && memoizedDefaultValues?.customer?.externalTMSID) {
      setCustomers((prevCustomers) =>
        injectSelectedObject(
          memoizedDefaultValues.customer,
          prevCustomers ?? []
        )
      );
    }
  }, [isLoadingCustomers, memoizedDefaultValues?.customer]);

  // Inject the selected locations so Select components can display them
  useEffect(() => {
    if (isLoadingLocations) {
      return;
    }

    const addtlLocs: TMSLocation[] = [];
    if (memoizedDefaultValues?.pickup?.externalTMSID) {
      addtlLocs.push(memoizedDefaultValues.pickup as TMSLocation);
    }
    if (memoizedDefaultValues?.consignee?.externalTMSID) {
      addtlLocs.push(memoizedDefaultValues.consignee as TMSLocation);
    }
    for (const stop of memoizedDefaultValues?.stops ?? []) {
      if (stop?.externalTMSID) {
        addtlLocs.push({
          ...(stop.address as unknown as TMSLocation),
          externalTMSID: stop.externalTMSID,
          name: stop.address?.name ?? '',
          addressLine1: stop.address?.addressLine1 ?? '',
          addressLine2: stop.address?.addressLine2 ?? '',
          city: stop.address?.city ?? '',
          state: stop.address?.state ?? '',
          zipCode: stop.address?.zip ?? '',
          country: stop.address?.country ?? '',
        });
      }
    }

    if (addtlLocs.length > 0) {
      setDataLocations((prevLocations) => {
        let updatedLocations = prevLocations ?? [];
        addtlLocs.forEach((loc) => {
          updatedLocations = injectSelectedObject(loc, updatedLocations);
        });
        return updatedLocations;
      });
    }
  }, [
    isLoadingLocations,
    memoizedDefaultValues?.pickup,
    memoizedDefaultValues?.consignee,
    memoizedDefaultValues?.stops,
  ]);

  useFocusLoadViewSuggestion(
    clickedSuggestion,
    load?.freightTrackingID,
    setActiveTabs,
    !isCreateMode,
    allTabs
  );

  useEffect(() => {
    if (!memoizedDefaultValues) {
      return;
    }

    if (!isCreateMode) {
      return;
    }

    reset(memoizedDefaultValues);

    // Surface missing required fields after AI hydrate (no submit).
    return scheduleLoadBuildingSuggestionValidation(
      activeLoadBuildingSuggestion?.id != null && builtLoad == null,
      () => {
        void trigger();
      }
    );
  }, [
    memoizedDefaultValues,
    isCreateMode,
    reset,
    trigger,
    activeLoadBuildingSuggestion?.id,
    builtLoad,
  ]);

  useEffect(() => {
    if (!memoizedDefaultValues || isCreateMode || !load) {
      return;
    }

    if (isLoadingCustomers || isLoadingLocations) {
      return;
    }

    const hasCustomerID = Boolean(customerExternalTMSID);
    const hasLocationIDs = Boolean(
      pickupExternalTMSID || consigneeExternalTMSID
    );

    if (hasCustomerID && (!customers || customers.length === 0)) {
      return;
    }
    if (hasLocationIDs && (!dataLocations || dataLocations.length === 0)) {
      return;
    }
    if (editResetKeyRef.current === editResetKey) {
      return;
    }

    reset(memoizedDefaultValues);
    editResetKeyRef.current = editResetKey;
    setEditHydrationEpoch((epoch) => epoch + 1);
  }, [
    memoizedDefaultValues,
    customerExternalTMSID,
    pickupExternalTMSID,
    consigneeExternalTMSID,
    editResetKey,
    isCreateMode,
    load,
    isLoadingCustomers,
    isLoadingLocations,
    customers,
    dataLocations,
    reset,
  ]);

  // Re-apply CI/Appt only after edit hydrate reset — not on catalog search/length churn.
  // Live stopCount is read via ref inside useApplyLoadViewSuggestion so dropoff appt
  // targets the current last stop without re-applying when the route length changes.
  useApplyLoadViewSuggestion(
    formMethods.setValue,
    clickedSuggestion,
    load?.freightTrackingID,
    !isCreateMode,
    !isCreateMode ? editHydrationEpoch : memoizedDefaultValues,
    watchedStops?.length ?? 0
  );

  useEffect(() => {
    const carrierID = memoizedDefaultValues?.carrier?.externalTMSID;
    const selectedObj = carriers?.find((c) => c.externalTMSID === carrierID);

    if (carrierID && carriers && memoizedDefaultValues?.carrier) {
      setCarriers((prevCarriers) =>
        injectSelectedObject(
          selectedObj ??
            ({
              name: memoizedDefaultValues.carrier?.name,
              externalTMSID: memoizedDefaultValues.carrier?.externalTMSID,
            } as TMSCarrier),
          prevCarriers ?? []
        )
      );
    }
    // Carrier inject when externalTMSID is stable; full carrier object identity churns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carriers, memoizedDefaultValues?.carrier?.externalTMSID]);

  // Create mode: restore from localStorage (nav away / back) and persist on change — see TurvoLoadForm.
  useEffect(() => {
    if (!isCreateMode || !threadId) {
      return undefined;
    }

    const savedState = FormStorageService.getFormState<
      NormalizedLoad,
      NormalizedLoad
    >(`${tmsName}_${threadId}`);

    const clickedSuggestionForStorage = isLoadBuildingSuggestionClicked
      ? (activeLoadBuildingSuggestion ?? clickedSuggestion)
      : null;

    if (savedState && savedState.threadID === threadId) {
      const hasLbClicked =
        clickedSuggestion?.pipeline === SuggestionPipelines.LoadBuilding;
      const activeLbId = activeLoadBuildingSuggestion?.id;
      const savedLbId = savedState.clickedSuggestion?.id;
      const sameSuggestionAsSaved =
        activeLbId != null && savedLbId != null && activeLbId === savedLbId;

      const shouldApplySavedValues = !hasLbClicked || sameSuggestionAsSaved;

      if (shouldApplySavedValues) {
        const restoreFingerprint = [
          threadId,
          hasLbClicked ? 'lb-clicked' : 'no-lb-click',
          sameSuggestionAsSaved ? `same:${savedLbId}` : 'not-same',
        ].join('|');

        const applyFromSaved = () => {
          const fieldNames = flattenLoadFormFieldPaths(
            savedState.values as Record<string, unknown>
          );
          const cleanFields = normalizeLoad(tmsName, createInitLoad());

          fieldNames.forEach((fieldName) => {
            const isDirty: Undef<boolean> = get(
              savedState.dirtyFields,
              fieldName
            );
            if (isDirty === undefined || !isDirty) {
              set(cleanFields, fieldName, get(savedState.values, fieldName));
            }
          });

          cleanFields.stops = ensureMcleodRouteStops({
            stops: cleanFields.stops,
            pickup: cleanFields.pickup,
            consignee: cleanFields.consignee,
          });

          formMethods.reset(cleanFields as NormalizedLoad, {
            keepDefaultValues: false,
            keepTouched: false,
            keepDirtyValues: false,
            keepDirty: false,
          });

          window.setTimeout(() => {
            fieldNames.forEach((fieldName) => {
              const isDirty: Undef<boolean> = get(
                savedState.dirtyFields,
                fieldName
              );

              if (isDirty) {
                formMethods.setValue(
                  fieldName as FieldPath<NormalizedLoad>,
                  get(savedState.values, fieldName),
                  {
                    shouldDirty: isDirty,
                    shouldTouch: true,
                  }
                );
              }
            });

            const customerID = savedState.values.customer?.externalTMSID;
            if (customerID) {
              setCustomers((prevCustomers) =>
                injectSelectedObject(
                  savedState.values.customer,
                  prevCustomers ?? []
                )
              );
            }

            // Pickup/consignee rows are merged into `mergedLocations` via useWatch after setValue runs.
          }, 100);
        };

        if (!hasLbClicked) {
          setShowAIHints(true);
          if (savedState.clickedSuggestion?.id != null) {
            goToSuggestion({
              suggestionID: savedState.clickedSuggestion.id,
            });
          }
        }

        if (
          loadBuildingStorageRestoreFingerprintRef.current !==
          restoreFingerprint
        ) {
          loadBuildingStorageRestoreFingerprintRef.current = restoreFingerprint;
          applyFromSaved();
        }
      }
    }

    const initialSaveTimer = window.setTimeout(() => {
      if (!savedState || threadId === savedState.threadID) {
        FormStorageService.saveFormState(`${tmsName}_${threadId}`, {
          threadID: threadId,
          clickedSuggestion: clickedSuggestionForStorage,
          values: formMethods.getValues(),
          dirtyFields: formMethods.formState.dirtyFields,
        });
      }
    }, 100);

    const subscription = formMethods.watch((value) => {
      if (!savedState || threadId === savedState.threadID) {
        FormStorageService.saveFormState(`${tmsName}_${threadId}`, {
          threadID: threadId,
          clickedSuggestion: clickedSuggestionForStorage,
          values: value,
          dirtyFields: formMethods.formState.dirtyFields,
        });
      }
    });

    return () => {
      window.clearTimeout(initialSaveTimer);
      subscription.unsubscribe();
    };
  }, [
    formMethods,
    threadId,
    isLoadBuildingSuggestionClicked,
    clickedSuggestion,
    activeLoadBuildingSuggestion,
    goToSuggestion,
    tmsName,
    isCreateMode,
  ]);

  function handleClearForm() {
    if (isCreateMode) {
      setShowAIHints(false);
      setBuiltLoad(null);
      setSharepointAgileCreated(undefined);
      setLoadCreatedBannerPoNums(undefined);
      setCreateNewLocForPickup(false);
      FormStorageService.clearFormState(`${tmsName}_${threadId}`);
      loadBuildingStorageRestoreFingerprintRef.current = '';

      setCurrentState((prevState) => ({
        ...prevState,
        clickedSuggestion: null,
      }));

      const emptyForm = initLoadBuildingForm(tmsName, user);
      formMethods.reset(emptyForm, {
        keepDefaultValues: false,
        keepDirty: false,
        keepDirtyValues: false,
        keepTouched: false,
      });

      formMethods.clearErrors();
    }
  }

  const onInvalid: SubmitErrorHandler<NormalizedLoad> = (errors) => {
    const flatErrors = formatFormErrors(errors);
    focusRouteStopFromValidationErrors(flatErrors);
    handleFormErrors(errors, {
      tmsName: TMS.McleodEnterprise,
      maxLabelsToShow: DEFAULT_MAX_VALIDATION_TOAST_LABELS,
      activeTabs,
      setActiveTabs,
      toast,
      routeStopCount: watchedStops?.length,
    });
  };

  const onSubmitCreate: SubmitHandler<NormalizedLoad> = async (data) => {
    setIsLoading(true);
    formMethods.clearErrors();

    const normalizedRouteStops = normalizeMcleodRouteStopTypes(
      data.stops ?? []
    );

    const applySubmitValidationErrors = (
      fieldErrors: ManualFieldError<NormalizedLoad>[]
    ) => {
      focusRouteStopFromValidationErrors(fieldErrors);
      applyManualValidationErrors(
        formMethods,
        fieldErrors,
        loadBuildingManualValidationDisplayOptions({
          tmsName: TMS.McleodEnterprise,
          activeTabs,
          setActiveTabs,
          toast,
          routeStopCount: normalizedRouteStops.length,
          afterErrorHandled: () => setIsLoading(false),
        })
      );
    };

    if (normalizedRouteStops.length < 2) {
      applySubmitValidationErrors([
        {
          path: 'stops.0.externalTMSID',
          message: 'Please provide at least a pickup and a consignee.',
        },
      ]);
      return;
    }

    const duplicateStops =
      validateDuplicateRouteStopLocations(normalizedRouteStops);
    if (!duplicateStops.ok) {
      applySubmitValidationErrors([
        {
          path: `stops.${duplicateStops.pathIndex}.externalTMSID` as FieldPath<NormalizedLoad>,
          message: duplicateStops.message,
        },
      ]);
      return;
    }

    const monotonicStops =
      validateMcleodMonotonicRouteStopTimes(normalizedRouteStops);
    if (!monotonicStops.ok) {
      applySubmitValidationErrors([
        {
          path: `stops.${monotonicStops.pathIndex}.apptStartTime` as FieldPath<NormalizedLoad>,
          message: monotonicStops.message,
        },
      ]);
      return;
    }

    const isMultiStopRoute = isMcleodMultiStopRoute(normalizedRouteStops);
    const { pickup, consignee } =
      syncMcleodRouteStopsToPickupConsignee(normalizedRouteStops);

    const stopsForApi = denormalizeStopsForTMSForm(
      tmsName,
      normalizedRouteStops
    );

    const form: NormalizedLoad = {
      ...data,
      freightTrackingID: '',
      externalTMSID: '',
      customer: { ...data.customer },
      billTo: { ...data.billTo },
      pickup: pickup ? { ...pickup } : data.pickup,
      consignee: consignee ? { ...consignee } : data.consignee,
      carrier: { ...data.carrier },
      rateData: { ...data.rateData },
      specifications: { ...data.specifications },
      poNums: isMultiStopRoute ? '' : data.poNums,
    };

    if (form.pickup && 'externalTMSStopID' in form.pickup) {
      delete (form.pickup as { externalTMSStopID?: string }).externalTMSStopID;
    }
    if (form.consignee && 'externalTMSStopID' in form.consignee) {
      delete (form.consignee as { externalTMSStopID?: string })
        .externalTMSStopID;
    }
    stopsForApi.forEach((stop) => {
      delete (stop as { externalTMSStopID?: string }).externalTMSStopID;
    });

    const partialRequest: CreateLoadRequest = {
      load: {
        ...form,
        tmsID: tmsID ?? 0,
        mode: form.mode,
        moreThanTwoStops: isMultiStopRoute,
        customer: denormalizeDatesForTMSForm(
          tmsName,
          form.customer
        ) as Customer,
        billTo: denormalizeDatesForTMSForm(tmsName, form.billTo),
        specifications: (() => {
          const specs = { ...form.specifications };
          if (specs?.orderType) {
            specs.orderType = convertOrderTypeLabelToCode(
              specs.orderType,
              mcleodIntegration
            ) as string;
          }
          return specs;
        })(),
        pickup: {
          ...(denormalizeDatesForTMSForm(tmsName, form.pickup) as Pickup),
          additionalReferences: form.pickup?.additionalReferences ?? [],
        } as Pickup,
        consignee: {
          ...(denormalizeDatesForTMSForm(tmsName, form.consignee) as Dropoff),
          additionalReferences: form.consignee?.additionalReferences ?? [],
        } as Dropoff,
        carrier: {
          ...denormalizeDatesForTMSForm(tmsName, form.carrier),
          rateConfirmationSent: form.carrier?.rateConfirmationSent
            ? true
            : false,
        } as LoadCarrier,
        rateData: denormalizeDatesForTMSForm(tmsName, form.rateData),
        stops: stopsForApi,
      } as unknown as Load,
    };

    const reqData = withLoadBuildingSuggestionContext(
      {
        clickedSuggestion: activeLoadBuildingSuggestion,
        threadId,
        emailId,
        emailExternalId: threadItemId,
      },
      partialRequest
    );

    const res = await guardedCreateLoad(data.customer, reqData, customers);
    if (!res) {
      setIsLoading(false);
      return;
    }

    if (res.isOk()) {
      setSharepointAgileCreated(
        sharepointAgileCreatedSummaryFromSuggestion(
          activeLoadBuildingSuggestion
        )
      );
      setLoadCreatedBannerPoNums(
        (
          activeLoadBuildingSuggestion as LoadBuildingSuggestions | null
        )?.suggested?.poNums?.trim() || undefined
      );
      setBuiltLoad(res.value.load);
      setCreateNewLocForPickup(false);
      FormStorageService.clearFormState(`${tmsName}_${threadId}`);

      if (
        clickedSuggestion &&
        clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
      ) {
        setCurrentState((prevState) => ({
          ...prevState,
          ...applyCompleteKitSuggestions(
            prevState,
            clickedSuggestion.id,
            SuggestionPipelines.LoadBuilding
          ),
        }));
      }

      onSuccess?.(res.value);
    } else {
      toast({
        description: res.error.message || 'Failed to create load.',
        variant: 'destructive',
      });
      onError?.(res.error);
    }
    setIsLoading(false);
  };

  const onSubmitEdit: SubmitHandler<NormalizedLoad> = async (form) => {
    if (!load) {
      return;
    }

    if (enableCarrierSection && !carrierIsQualified) {
      toast({
        description: `${form.carrier?.name ?? ''} is not qualified for this load.`,
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    formMethods.clearErrors();

    const normalizedRouteStops = normalizeMcleodRouteStopTypes(
      form.stops ?? []
    );

    const applySubmitValidationErrors = (
      fieldErrors: ManualFieldError<NormalizedLoad>[]
    ) => {
      focusRouteStopFromValidationErrors(fieldErrors);
      applyManualValidationErrors(
        formMethods,
        fieldErrors,
        loadBuildingManualValidationDisplayOptions({
          tmsName: TMS.McleodEnterprise,
          activeTabs,
          setActiveTabs,
          toast,
          routeStopCount: normalizedRouteStops.length,
          afterErrorHandled: () => setIsLoading(false),
        })
      );
    };

    if (normalizedRouteStops.length < 2) {
      applySubmitValidationErrors([
        {
          path: 'stops.0.externalTMSID',
          message: 'Please provide at least a pickup and a consignee.',
        },
      ]);
      return;
    }

    const duplicateStops =
      validateDuplicateRouteStopLocations(normalizedRouteStops);
    if (!duplicateStops.ok) {
      applySubmitValidationErrors([
        {
          path: `stops.${duplicateStops.pathIndex}.externalTMSID` as FieldPath<NormalizedLoad>,
          message: duplicateStops.message,
        },
      ]);
      return;
    }

    const monotonicStops =
      validateMcleodMonotonicRouteStopTimes(normalizedRouteStops);
    if (!monotonicStops.ok) {
      applySubmitValidationErrors([
        {
          path: `stops.${monotonicStops.pathIndex}.apptStartTime` as FieldPath<NormalizedLoad>,
          message: monotonicStops.message,
        },
      ]);
      return;
    }

    const isMultiStopRoute = isMcleodMultiStopRoute(normalizedRouteStops);
    const { pickup, consignee } =
      syncMcleodRouteStopsToPickupConsignee(normalizedRouteStops);
    const stops = denormalizeStopsForTMSForm(tmsName, normalizedRouteStops);

    const reqData: UpdateTMSRequest = {
      operation: TMSOperation.UpdateLoad,
      load: {
        ...load,
        mode: form.mode,
        freightTrackingID: form.freightTrackingID || load.freightTrackingID,
        externalTMSID: form.externalTMSID || load.externalTMSID,
        operator:
          form.operator !== undefined && form.operator !== null
            ? form.operator
            : load.operator || '',
        moreThanTwoStops: isMultiStopRoute,
        poNums: isMultiStopRoute ? '' : (form.poNums ?? load.poNums ?? ''),
        customer: {
          ...load.customer,
          ...denormalizeDatesForTMSForm(tmsName, form.customer),
        } as Customer,
        billTo: {
          ...load.billTo,
          ...denormalizeDatesForTMSForm(tmsName, form.billTo),
        } as CompanyCoreInfo,
        specifications: (() => {
          const denormalizedSpecs = {
            ...load.specifications,
            ...denormalizeDatesForTMSForm(tmsName, form.specifications),
          } as Specifications;
          if (denormalizedSpecs.orderType) {
            denormalizedSpecs.orderType = convertOrderTypeLabelToCode(
              denormalizedSpecs.orderType,
              mcleodIntegration
            ) as string;
          }
          return denormalizedSpecs;
        })(),
        pickup: {
          ...load.pickup,
          ...denormalizeDatesForTMSForm(tmsName, pickup),
          externalTMSID:
            pickup?.externalTMSID ??
            pickup?.name ??
            load.pickup?.externalTMSID ??
            '',
          additionalReferences: pickup?.additionalReferences ?? [],
        } as Pickup,
        consignee: {
          ...load.consignee,
          ...denormalizeDatesForTMSForm(tmsName, consignee),
          externalTMSID:
            consignee?.externalTMSID ??
            consignee?.name ??
            load.consignee?.externalTMSID ??
            '',
          additionalReferences: consignee?.additionalReferences ?? [],
        } as Dropoff,
        carrier: {
          ...load.carrier,
          ...denormalizeDatesForTMSForm(tmsName, form.carrier),
          rateConfirmationSent: form.carrier?.rateConfirmationSent
            ? true
            : false,
        } as LoadCarrier,
        rateData: (() => {
          const denormalizedRateData = {
            ...load.rateData,
            ...denormalizeDatesForTMSForm(tmsName, form.rateData),
          } as RateData;

          // Ensure customerRateNumUnits > 0 when rate type is not "Flat"
          const customerRateType =
            denormalizedRateData.customerRateType?.toLowerCase();
          if (
            customerRateType &&
            customerRateType !== 'f' &&
            customerRateType !== 'flat' &&
            (!denormalizedRateData.customerRateNumUnits ||
              denormalizedRateData.customerRateNumUnits <= 0)
          ) {
            denormalizedRateData.customerRateNumUnits =
              load.rateData?.customerRateNumUnits &&
              load.rateData.customerRateNumUnits > 0
                ? load.rateData.customerRateNumUnits
                : 1;
          }

          return denormalizedRateData;
        })(),
        additionalReferences:
          form.additionalReferences || load.additionalReferences || [],
        stops,
      },
    };

    const res = await updateTMS(load.ID!, reqData);

    if (res.isOk()) {
      toast({
        description: res.value.message,
        variant: 'success',
      });
      onSuccess?.(res.value);
      await acceptLoadViewSuggestion(
        clickedSuggestion,
        {
          carrier: reqData.load.carrier ?? load.carrier,
          pickup: reqData.load.pickup,
          consignee: reqData.load.consignee as Load['consignee'],
        },
        setCurrentState,
        undefined,
        tmsName
      );
    } else {
      toast({
        description: res.error.message,
        variant: 'destructive',
      });
      onError?.(res.error);
    }

    setIsLoading(false);
  };

  const handleFormSubmit: SubmitHandler<NormalizedLoad> = async (data) => {
    if (onSubmit) {
      onSubmit(data);
    } else if (isCreateMode) {
      await onSubmitCreate(data);
    } else {
      await onSubmitEdit(data);
    }
  };

  const buttonText = isCreateMode ? ButtonText.BuildLoad : ButtonText.UpdateTMS;
  const buttonNamePosthog = isCreateMode
    ? ButtonNamePosthog.BuildLoad
    : ButtonNamePosthog.UpdateTMS;

  const submitLogProperties = (() => {
    if (isCreateMode) {
      return { serviceID };
    }
    if (!load) {
      return undefined;
    }
    return {
      loadID: load.ID,
      freightTrackingID: load.freightTrackingID,
      serviceID: load.serviceID,
    };
  })();

  const carrierSectionLoad: Load = load ?? createInitLoad();

  return (
    <LoadContextProvider
      disabled={isCreateMode}
      fieldAttributes={loadContext.fieldAttributes}
      loadAttrsObj={loadContext.loadAttrsObj}
      invalidateLoad={loadContext.invalidateLoad}
    >
      <TMSProvider tmsID={tmsID ?? 0} tmsName={tmsName} tenant={tenant}>
        <Flex direction='col' className='mb-5 w-full'>
          <ExtendedFormProvider
            aiDefaultValues={showAIHints}
            aiIconOnly={true}
            highlightDirtyFields={!isCreateMode}
          >
            <FormProvider {...formMethods}>
              <form
                onSubmit={handleSubmit(handleFormSubmit, onInvalid)}
                className='w-full'
              >
                <LoadBuildingAccordionControls
                  activeTabs={activeTabs}
                  allTabs={allTabs}
                  onExpandAll={expandAll}
                  onCollapseAll={collapseAll}
                  defaultView={defaultView}
                  onDefaultViewChange={setDefaultView}
                />
                <Accordion
                  type='multiple'
                  value={activeTabs}
                  onValueChange={handleAccordionValueChange}
                  className='w-full'
                >
                  <LoadSectionAccordionItem
                    label='Customer'
                    icon={<Building2 className='h-6 w-6' strokeWidth={1} />}
                    name={AvailableTabs.customer}
                    activeTabs={activeTabs}
                  >
                    <CustomerSectionForm
                      formMethods={formMethods}
                      customers={customers}
                      setCustomers={setCustomers}
                      isLoadingCustomers={isLoadingCustomers}
                      setIsLoadingCustomers={setIsLoadingCustomers}
                      originalSuggestionData={
                        activeLoadBuildingSuggestion?.suggested as NormalizedLoad
                      }
                      showLoadLevelPo={
                        !isMcleodMultiStopRoute(watchedStops ?? [])
                      }
                    />
                  </LoadSectionAccordionItem>

                  <LoadSectionAccordionItem
                    label='Specs'
                    icon={<Weight className='h-6 w-6' strokeWidth={1} />}
                    name={AvailableTabs.specifications}
                    activeTabs={activeTabs}
                  >
                    <SpecificationsForm
                      formMethods={formMethods}
                      transportTypeOptions={transportTypeOptions}
                    />
                  </LoadSectionAccordionItem>

                  <LoadSectionAccordionItem
                    label='Rates'
                    icon={
                      <CircleDollarSignIcon
                        className='h-6 w-6'
                        strokeWidth={1}
                      />
                    }
                    name={AvailableTabs.rates}
                    activeTabs={activeTabs}
                  >
                    <RatesForm
                      formMethods={formMethods}
                      showCarrierFields={!isCreateMode}
                    />
                  </LoadSectionAccordionItem>

                  <McleodRouteSection
                    formMethods={formMethods}
                    activeTabs={activeTabs}
                    activeStopTab={activeRouteStopTab}
                    activeStopTabFocusKey={routeStopFocusKey}
                    onActiveStopTabHandled={handleActiveRouteStopTabHandled}
                    isLoadingLocations={isLoadingLocations}
                    locations={mergedLocations}
                    handleRefreshLocations={handleRefreshLocations}
                    setLocations={setLocationsAction}
                    pickupAiHighlightedExternalTMSID={
                      (
                        activeLoadBuildingSuggestion?.suggested as NormalizedLoad
                      )?.pickup?.externalTMSID
                    }
                    consigneeAiHighlightedExternalTMSID={
                      (
                        activeLoadBuildingSuggestion?.suggested as NormalizedLoad
                      )?.consignee?.externalTMSID
                    }
                    // McLeod only takes stop notes on new stops, and HC/BC are Trident's comment types
                    showStopComments={
                      isCreateMode && tenant.includes(Tenant.Trident)
                    }
                  />

                  {enableCarrierSection && (
                    <LoadSectionAccordionItem
                      label='Carrier'
                      icon={<TruckIcon className='h-6 w-6' strokeWidth={1} />}
                      name={AvailableTabs.carrier}
                      activeTabs={activeTabs}
                    >
                      <CarrierSectionForm
                        carrierNameRequired={false}
                        load={carrierSectionLoad}
                        suggestedFieldsPrevValues={undefined}
                        formMethods={formMethods}
                        isLoadingCarriers={isLoadingCarriers}
                        carriers={carriers}
                        setIsLoadingCarriers={setIsLoadingCarriers}
                        setCarriers={setCarriers}
                        setIsCarrierQualified={setCarrierIsQualified}
                        hideCarrierTimeInputs={false}
                      />
                    </LoadSectionAccordionItem>
                  )}

                  {!isCreateMode && isOperatorEnabled && (
                    <LoadSectionAccordionItem
                      label='Operator'
                      icon={
                        <CircleUserRound className='h-6 w-6' strokeWidth={1} />
                      }
                      name={AvailableTabs.operator}
                      activeTabs={activeTabs}
                    >
                      <OperatorSectionForm formMethods={formMethods} />
                    </LoadSectionAccordionItem>
                  )}
                </Accordion>

                <Flex direction='col' className='w-full mt-4'>
                  {isCreateMode && createNewLocForPickup && watchedPickup && (
                    <Flex
                      direction='row'
                      align='start'
                      gap='sm'
                      className='bg-info-200 rounded-lg py-2 px-2 mb-3'
                    >
                      <Info
                        className='h-4 w-4 shrink-0 mt-0.5 text-neutral-600'
                        strokeWidth={2.5}
                      />
                      <Typography
                        variant='body-xs'
                        className='text-neutral-700'
                      >
                        Submitting will create a new pickup location in McLeod
                        for: {concatAddress(watchedPickup)}
                      </Typography>
                    </Flex>
                  )}
                  <Button
                    buttonNamePosthog={buttonNamePosthog}
                    type='submit'
                    className='w-full'
                    disabled={isLoading}
                    logProperties={submitLogProperties}
                  >
                    {isLoading ? <ButtonLoader /> : buttonText}
                  </Button>

                  {isCreateMode && formMethods.formState.isDirty && (
                    <Flex
                      direction='row'
                      justify='center'
                      align='center'
                      className='w-full'
                    >
                      <Button
                        buttonNamePosthog={ButtonNamePosthog.ClearForm}
                        type='button'
                        className='mt-4 h-8 text-sm text-neutral-700'
                        disabled={isLoading}
                        variant='outline'
                        onClick={handleClearForm}
                      >
                        {ButtonText.ClearForm}
                      </Button>
                    </Flex>
                  )}

                  {isCreateMode &&
                    builtLoad &&
                    (builtLoad.freightTrackingID ||
                      builtLoad.externalTMSID) && (
                      <LoadCreatedMessage
                        builtLoadId={
                          builtLoad.freightTrackingID ?? builtLoad.externalTMSID
                        }
                        poNumber={
                          builtLoad?.poNums?.trim() ||
                          loadCreatedBannerPoNums?.trim() ||
                          undefined
                        }
                        sharepointAgileCreated={sharepointAgileCreated}
                      />
                    )}
                </Flex>
              </form>
            </FormProvider>
          </ExtendedFormProvider>
        </Flex>
      </TMSProvider>
    </LoadContextProvider>
  );
}
